/**
 * Delete Configuration Constants (Client-Side)
 * 
 * Shared constants for the unified delete system.
 * These constants mirror the server-side configuration.
 */

// Delete Entity Types
export const DELETE_ENTITY_TYPES = {
  // Book-related entities (user-generated content)
  LIT_GROUP: 'lit-group',
  BOOK: 'book',
  CHARACTER: 'character',
  VARIATION: 'variation',
  SPEECH_SAMPLE: 'speech-sample',
  TEXT_SNIPPET: 'text-snippet',
  VOICE_SNIPPET: 'voice-snippet',

  // Actor-related entities
  VOICE_ACTOR: 'voice-actor',
  ACTOR_PORTFOLIO: 'actor-portfolio',
  VOICE_ACTOR_ASSIGNMENT: 'voice-actor-assignment',

  // VoiceActor-specific entities
  VA_CHARACTER: 'va-character',
  VA_VARIATION: 'va-variation',
  VA_SPEECH_SAMPLE: 'va-speech-sample',
  VA_CHARACTER_AVATAR: 'va-character-avatar',

  // Opportunity & Application entities
  OPPORTUNITY: 'opportunity',
  APPLICATION: 'application',

  // System entities
  NOTIFICATION: 'notification',
  ARTICLE: 'article',
  CHARACTER_AVATAR: 'character-avatar',

  // Profile entities
  BOOK_PROFILE: 'book-profile'
};

// Delete Behavior Flags
export const DELETE_BEHAVIORS = {
  // Cleanup behaviors
  CLEANUP_S3: 'cleanup',           // Delete associated S3 files
  NO_CLEANUP: 'no-cleanup',        // Database only

  // Cascade behaviors
  CASCADE_ALL: 'cascade',          // Delete all children
  CASCADE_NONE: 'no-cascade',      // Delete entity only
  CASCADE_ORPHANS: 'cascade-orphans', // Delete only orphaned children

  // Safety behaviors
  FORCE_DELETE: 'force',           // Override safety checks
  SAFE_DELETE: 'safe',             // Respect references

  // Verification behaviors
  DRY_RUN: 'dry-run',             // Return what would be deleted
  EXECUTE: 'execute'               // Actually perform deletion
};

// Delete Response Types
export const DELETE_RESPONSE_TYPES = {
  SUCCESS: 'success',
  PARTIAL_SUCCESS: 'partial_success',
  FAILURE: 'failure',
  DRY_RUN_RESULT: 'dry_run_result'
};

// Authorization Levels
export const DELETE_AUTH_LEVELS = {
  OWNER: 'owner',           // User owns the entity
  ADMIN: 'admin',           // Admin privileges required
  MODERATOR: 'moderator',   // Moderator privileges
  PUBLIC: 'public'          // No special auth required
};

// Validation Rules
export const DELETE_VALIDATION_RULES = {
  ENTITY_ID_PATTERN: /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
  MAX_CASCADE_DEPTH: 5,
  MAX_S3_FILES_PER_REQUEST: 1000,
  TIMEOUT_SECONDS: 300
};

// Delete Endpoints
export const DELETE_ENDPOINTS = {
  // Primary unified endpoint
  UNIFIED_DELETE: '/delete/:entityType/:entityId',

  // Batch operations
  BATCH_DELETE: '/delete/batch',

  // Dry run endpoint
  DRY_RUN_DELETE: '/delete/dry-run/:entityType/:entityId',

  // Configuration endpoints
  DELETE_CONFIG: '/delete/config',
  DELETE_CONFIG_BY_TYPE: '/delete/config/:entityType'
};

// Delete Action Types for Redux
export const DELETE_ACTION_TYPES = {
  DELETE_ENTITY_REQUEST: 'DELETE_ENTITY_REQUEST',
  DELETE_ENTITY_SUCCESS: 'DELETE_ENTITY_SUCCESS',
  DELETE_ENTITY_FAILURE: 'DELETE_ENTITY_FAILURE',
  DELETE_DRY_RUN_SUCCESS: 'DELETE_DRY_RUN_SUCCESS',
  CLEAR_DELETE_RESULTS: 'CLEAR_DELETE_RESULTS',
  SET_DELETE_PROGRESS: 'SET_DELETE_PROGRESS',
  CLEAR_DELETE_PROGRESS: 'CLEAR_DELETE_PROGRESS'
};

// Standard Delete Result Structure
export const DELETE_RESULT_STRUCTURE = {
  status: 'string',              // SUCCESS | PARTIAL_SUCCESS | FAILURE
  entityType: 'string',          // The entity type that was deleted
  entityId: 'string',            // The ID of the deleted entity
  deletedCount: 'number',        // Number of database records deleted
  s3FilesDeleted: 'array',       // Array of S3 files that were deleted
  s3FilesFailed: 'array',        // Array of S3 files that failed to delete
  cascadeResults: 'object',      // Results of cascaded deletions
  errors: 'array',               // Any errors encountered
  warnings: 'array',             // Any warnings
  dryRunResults: 'object'        // What would be deleted (dry run only)
};

// Client-specific Delete API Interface
export const DELETE_API_METHODS = {
  // Primary method
  deleteEntity: (entityType, entityId, options = {}) => {
    const {
      cleanup = DELETE_BEHAVIORS.CLEANUP_S3,
      cascade = DELETE_BEHAVIORS.CASCADE_ALL,
      dryRun = false,
      force = false
    } = options;

    const endpoint = DELETE_ENDPOINTS.UNIFIED_DELETE
      .replace(':entityType', entityType)
      .replace(':entityId', entityId);

    return {
      method: 'DELETE',
      url: endpoint,
      params: { cleanup, cascade, dryRun, force }
    };
  },

  // Convenience methods
  deleteVariation: (variationId, options) =>
    DELETE_API_METHODS.deleteEntity(DELETE_ENTITY_TYPES.VARIATION, variationId, options),

  deleteCharacter: (characterId, options) =>
    DELETE_API_METHODS.deleteEntity(DELETE_ENTITY_TYPES.CHARACTER, characterId, options),

  deleteSpeechSample: (sampleId, options) =>
    DELETE_API_METHODS.deleteEntity(DELETE_ENTITY_TYPES.SPEECH_SAMPLE, sampleId, options),

  deleteLitGroup: (litGroupId, options) =>
    DELETE_API_METHODS.deleteEntity(DELETE_ENTITY_TYPES.LIT_GROUP, litGroupId, options),

  deleteBook: (bookId, options) =>
    DELETE_API_METHODS.deleteEntity(DELETE_ENTITY_TYPES.BOOK, bookId, options),

  // VoiceActor convenience methods
  deleteVACharacter: (characterId, options) =>
    DELETE_API_METHODS.deleteEntity(DELETE_ENTITY_TYPES.VA_CHARACTER, characterId, options),

  deleteVAVariation: (variationId, options) =>
    DELETE_API_METHODS.deleteEntity(DELETE_ENTITY_TYPES.VA_VARIATION, variationId, options),

  deleteVASpeechSample: (sampleId, options) =>
    DELETE_API_METHODS.deleteEntity(DELETE_ENTITY_TYPES.VA_SPEECH_SAMPLE, sampleId, options),

  deleteVACharacterAvatar: (characterId, options) =>
    DELETE_API_METHODS.deleteEntity(DELETE_ENTITY_TYPES.VA_CHARACTER_AVATAR, characterId, options)
};

// Entity-specific Delete Configurations (Client-side subset)
export const ENTITY_DELETE_CONFIGS = {
  [DELETE_ENTITY_TYPES.LIT_GROUP]: {
    defaultBehavior: DELETE_BEHAVIORS.CASCADE_ALL,
    supportedBehaviors: [
      DELETE_BEHAVIORS.CLEANUP_S3,
      DELETE_BEHAVIORS.CASCADE_ALL,
      DELETE_BEHAVIORS.CASCADE_NONE,
      DELETE_BEHAVIORS.DRY_RUN
    ],
    requiresConfirmation: true,
    confirmationMessage: 'This will delete the literary group and all associated books, characters, and variations. This action cannot be undone.',
    cascadeTargets: ['books', 'characters', 'variations', 'speech-samples']
  },

  [DELETE_ENTITY_TYPES.BOOK]: {
    defaultBehavior: DELETE_BEHAVIORS.CASCADE_ALL,
    supportedBehaviors: [
      DELETE_BEHAVIORS.CLEANUP_S3,
      DELETE_BEHAVIORS.CASCADE_ALL,
      DELETE_BEHAVIORS.CASCADE_ORPHANS,
      DELETE_BEHAVIORS.DRY_RUN
    ],
    requiresConfirmation: true,
    confirmationMessage: 'This will delete the book and all associated characters and variations. This action cannot be undone.',
    cascadeTargets: ['characters', 'variations', 'speech-samples']
  },

  [DELETE_ENTITY_TYPES.CHARACTER]: {
    defaultBehavior: DELETE_BEHAVIORS.CASCADE_ALL,
    supportedBehaviors: [
      DELETE_BEHAVIORS.CLEANUP_S3,
      DELETE_BEHAVIORS.CASCADE_ALL,
      DELETE_BEHAVIORS.CASCADE_ORPHANS,
      DELETE_BEHAVIORS.DRY_RUN
    ],
    requiresConfirmation: true,
    confirmationMessage: 'This will delete the character and all associated variations and speech samples. This action cannot be undone.',
    cascadeTargets: ['variations', 'speech-samples']
  },

  [DELETE_ENTITY_TYPES.VARIATION]: {
    defaultBehavior: DELETE_BEHAVIORS.CLEANUP_S3,
    supportedBehaviors: [
      DELETE_BEHAVIORS.CLEANUP_S3,
      DELETE_BEHAVIORS.NO_CLEANUP,
      DELETE_BEHAVIORS.CASCADE_ALL,
      DELETE_BEHAVIORS.DRY_RUN
    ],
    requiresConfirmation: true,
    confirmationMessage: 'This will delete the variation and all associated speech samples. This action cannot be undone.',
    cascadeTargets: ['speech-samples']
  },

  [DELETE_ENTITY_TYPES.SPEECH_SAMPLE]: {
    defaultBehavior: DELETE_BEHAVIORS.CLEANUP_S3,
    supportedBehaviors: [
      DELETE_BEHAVIORS.CLEANUP_S3,
      DELETE_BEHAVIORS.NO_CLEANUP,
      DELETE_BEHAVIORS.SAFE_DELETE,
      DELETE_BEHAVIORS.DRY_RUN
    ],
    requiresConfirmation: true,
    confirmationMessage: 'This will delete the speech sample and associated audio files. This action cannot be undone.',
    cascadeTargets: ['text-snippets', 'voice-snippets']
  },

  [DELETE_ENTITY_TYPES.NOTIFICATION]: {
    defaultBehavior: DELETE_BEHAVIORS.CASCADE_NONE,
    supportedBehaviors: [
      DELETE_BEHAVIORS.CASCADE_NONE,
      DELETE_BEHAVIORS.DRY_RUN
    ],
    requiresConfirmation: false,
    confirmationMessage: null,
    cascadeTargets: []
  }
};

// Progress tracking steps
export const DELETE_PROGRESS_STEPS = [
  { step: 'validating', message: 'Validating deletion request...' },
  { step: 'authorizing', message: 'Checking permissions...' },
  { step: 'analyzing', message: 'Analyzing files to delete...' },
  { step: 'deleting-db', message: 'Deleting database records...' },
  { step: 'cleaning-s3', message: 'Cleaning up S3 files...' },
  { step: 'updating-cache', message: 'Updating cache...' },
  { step: 'completed', message: 'Deletion completed successfully' }
];

// Error handling patterns
export const DELETE_ERROR_TYPES = {
  NETWORK_ERROR: 'network_error',
  AUTH_ERROR: 'auth_error',
  VALIDATION_ERROR: 'validation_error',
  PARTIAL_ERROR: 'partial_error',
  S3_ERROR: 's3_error',
  UNKNOWN_ERROR: 'unknown_error'
};

export const DELETE_ERROR_MESSAGES = {
  [DELETE_ERROR_TYPES.NETWORK_ERROR]: 'Network error occurred. Please check your connection and try again.',
  [DELETE_ERROR_TYPES.AUTH_ERROR]: 'You do not have permission to delete this item.',
  [DELETE_ERROR_TYPES.VALIDATION_ERROR]: 'Invalid delete request. Please check the parameters.',
  [DELETE_ERROR_TYPES.PARTIAL_ERROR]: 'Some items were deleted successfully, but others failed.',
  [DELETE_ERROR_TYPES.S3_ERROR]: 'Database updated successfully, but some files could not be deleted.',
  [DELETE_ERROR_TYPES.UNKNOWN_ERROR]: 'An unexpected error occurred. Please try again.'
};

// Legacy endpoint mappings for migration reference
export const LEGACY_DELETE_MAPPINGS = {
  '/api/variations/:id': {
    newEndpoint: '/delete/variation/:id',
    defaultOptions: { cleanup: 'cleanup', cascade: 'cascade' }
  },
  '/api/delete-character_speech_sample/:targetUuid': {
    newEndpoint: '/delete/speech-sample/:id',
    defaultOptions: { cleanup: 'cleanup', cascade: 'cascade' }
  },
  '/api/delete-book': {
    newEndpoint: '/delete/book/:id',
    defaultOptions: { cleanup: 'cleanup', cascade: 'cascade' }
  },
  '/api/delete-literary-group': {
    newEndpoint: '/delete/lit-group/:id',
    defaultOptions: { cleanup: 'cleanup', cascade: 'cascade' }
  }
};
